import React, { useState, useEffect } from 'react';
import Plotly from 'react-plotly.js';
import { getChartColors, getChartTitle, getChartAxis, getChartLegend, getChartFont } from '../../utils/chartColors';

const LineChartWithLegendZeroStart = ({ 
  data, 
  title, 
  subtitle,
  xKey, 
  yKeys, 
  colors = ['#f97316', '#3b82f6', '#8b5cf6'],
  showLegend = true,
  formatCurrency = false,
  xLabel = 'Date',
  yLabel = 'Value',
  logScale = false,
  fullHeight = false,
  legendLabels = null
}) => {
  const [themeKey, setThemeKey] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Listen for theme changes and window resize
  useEffect(() => {
    const checkThemeChange = () => {
      setThemeKey(prev => prev + 1);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setThemeKey(prev => prev + 1); // Force re-render on resize
    };

    // Listen for DOM mutations (theme class changes)
    const observer = new MutationObserver(checkThemeChange);
    const landingPage = document.querySelector('.landing-page');
    if (landingPage) {
      observer.observe(landingPage, { attributes: true, attributeFilter: ['class'] });
    }

    // Listen for window resize
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div>Loading...</div>;
  }

  // Prepare data for Plotly
  const plotData = yKeys.map((yKey, index) => {
    const values = data.map(d => d[yKey] || 0);
    const colorConfig = colors[index] || colors[0];
    const color = typeof colorConfig === 'object' ? colorConfig.color : colorConfig;
    const dash = typeof colorConfig === 'object' ? colorConfig.dash : 'solid';
    const fill = typeof colorConfig === 'object' && colorConfig.fill ? colorConfig.fill : false;
    const lineWidth = typeof colorConfig === 'object' && colorConfig.lineWidth !== undefined ? colorConfig.lineWidth : 3;
    
    // Convert hex color to rgba for fill if needed
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    const trace = {
      type: 'scatter',
      mode: 'lines',
      name: legendLabels && legendLabels[index] ? legendLabels[index] : yKey.replace(/_/g, ' ').toUpperCase(),
      x: data.map(d => new Date(d[xKey])),
      y: values,
      line: { 
        color: color, 
        width: lineWidth,
        dash: dash
      },
      hovertemplate: `<b>%{fullData.name}</b><br>Date: %{x}<br>Value: ${formatCurrency ? '$%{y:,.0f}' : '%{y}'}<extra></extra>`
    };
    
    // Add fill for area charts
    if (fill) {
      trace.fill = fill === true ? 'tozeroy' : fill;
      trace.fillcolor = hexToRgba(color, 0.15); // 15% opacity for area fill (more transparent)
    }
    
    return trace;
  });

  // Sort plotData alphabetically by name for consistent legend ordering
  if (showLegend && plotData.length > 1) {
    plotData.sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }

  const isMobile = windowWidth <= 768;
  
  // Calculate max value across all series to set appropriate y-axis range
  let maxValue = 0;
  yKeys.forEach(yKey => {
    const values = data.map(d => d[yKey] || 0).filter(v => v !== null && v !== undefined);
    if (values.length > 0) {
      const seriesMax = Math.max(...values);
      if (seriesMax > maxValue) maxValue = seriesMax;
    }
  });
  
  // Set y-axis range to start at 0, with some padding at the top (10% padding)
  const yAxisRange = [0, maxValue * 1.1];
  
  // Prepare title configuration
  const titleConfig = getChartTitle(title);
  
  // Prepare annotations for subtitle if provided
  const annotations = subtitle ? [{
    text: subtitle,
    xref: 'paper',
    yref: 'paper',
    x: 0.02,
    y: titleConfig.y - 0.08, // Position further below title to avoid overlap
    xanchor: 'left',
    yanchor: 'top',
    showarrow: false,
    font: {
      ...getChartFont(12),
      size: 10,
      color: getChartColors().textSecondary
    }
  }] : [];
  
  const layout = {
    title: titleConfig,
    annotations: annotations,
    showlegend: showLegend,
    legend: {
      ...getChartLegend(yKeys.length, windowWidth),
      traceorder: 'normal' // Use the order of traces in the data array
    },
    xaxis: getChartAxis(xLabel, true),
    yaxis: {
      ...getChartAxis(yLabel, false),
      type: logScale ? 'log' : 'linear',
      range: yAxisRange // Force y-axis to start at 0
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    margin: {
      l: 80,
      r: isMobile ? 20 : 100,
      t: subtitle ? 110 : 80, // Extra space for subtitle to prevent overlap
      b: isMobile ? (yKeys.length > 6 ? 140 : 120) : 40
    },
    height: 400,
    autosize: true
  };

  const config = {
    displayModeBar: false,
    responsive: true
  };

  return (
    <div style={{ width: '100%', height: fullHeight ? '100%' : '400px' }}>
      <Plotly
        key={themeKey}
        data={plotData}
        layout={layout}
        config={{
          ...config,
          responsive: true,
          useResizeHandler: true
        }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler={true}
      />
    </div>
  );
};

export default LineChartWithLegendZeroStart;

